/** Single chat message row — chat-bubble layout matching OneStone dashboard.
 *  Uses Markdown for the agent's text and ToolCallDetails for tool calls (media
 *  embeds come only from media tools' results — `MEDIA_TOOL_NAMES`). */
import { User, AlertTriangle, X } from 'lucide-react';
import { memo } from 'react';
import type { ChatMessage } from '@/lib/chatStore';
import { BASE_URL } from '@/lib/api';
import { useOperator } from '@/lib/useOperator';
import { relativeTime } from '@/lib/time';
import { cn } from '@onestone/ui/utils';
import { Markdown } from './Markdown';
import { ToolCallDetails } from './ToolCallDetails';
import { UserAttachment } from './UserAttachment';

export const MessageRow = memo(function MessageRow({
  message,
  onDismiss,
}: {
  message: ChatMessage;
  onDismiss?: () => void;
}) {
  const { avatarUrl, displayName } = useOperator();
  if (message.role === 'system') {
    return (
      <div className="flex animate-[slide-up_0.15s_ease-out] justify-center px-4 py-2">
        <div className="flex max-w-[90%] items-start gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <span className="flex-1 whitespace-pre-wrap">{message.content}</span>
          {onDismiss ? (
            <button
              type="button"
              className="shrink-0 rounded-sm p-0.5 hover:bg-muted"
              onClick={onDismiss}
              aria-label="Dismiss"
            >
              <X className="h-3 w-3" />
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (message.role === 'checkpoint') {
    // What the agent is sent instead of the first `covers` messages of the log;
    // the messages themselves stay here. Messages after them (the turns kept
    // word for word, and any that arrived while it was written) may sit above
    // this row, so the label names its reach rather than saying "above".
    return (
      <details data-testid="message-checkpoint" className="rounded-lg border border-border bg-card px-4 py-3 open:pb-3">
        <summary className="flex cursor-pointer items-baseline justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {message.covers !== undefined
              ? `Checkpoint — summary of the first ${message.covers} messages; the agent is sent this instead of them`
              : 'Checkpoint — summary of the earlier conversation; the agent is sent this instead of it'}
          </span>
          {message.timestamp ? (
            <span className="text-xs text-muted-foreground/80">{relativeTime(message.timestamp)}</span>
          ) : null}
        </summary>
        <p className="mt-1.5 whitespace-pre-wrap text-sm text-foreground/90">{message.content}</p>
      </details>
    );
  }

  if (message.role === 'error') {
    return (
      <div className="flex animate-[slide-up_0.15s_ease-out] justify-center px-4 py-3">
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>{message.content}</span>
        </div>
      </div>
    );
  }

  const isUser = message.role === 'user';

  return (
    <div
      data-testid={isUser ? 'message-user' : 'message-assistant'}
      className={cn(
        'relative flex animate-[slide-up_0.15s_ease-out]',
        isUser ? 'justify-end pt-2.5' : 'justify-start pt-2.5',
      )}
    >
      {/* Avatar circle */}
      <div
        className={cn(
          'absolute -top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-background',
          isUser
            ? 'right-0 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground'
            : 'left-0 bg-muted text-muted-foreground',
        )}
        aria-hidden="true"
      >
        {isUser ? (
          avatarUrl ? (
            <img
              src={`${BASE_URL}${avatarUrl}`}
              alt={displayName}
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            <User className="h-2.5 w-2.5" />
          )
        ) : (
          <img src="./logo-256.png" alt="OneStone" className="h-full w-full rounded-full" />
        )}
      </div>

      {/* Message bubble */}
      <div
        className={cn(
          'min-w-0 max-w-[96%] rounded-2xl px-3.5 py-2.5 text-sm',
          isUser ? 'rounded-tr-md bg-primary text-primary-foreground' : 'rounded-tl-md border border-border bg-card',
        )}
      >
        {isUser ? (
          <div className="flex flex-col gap-2">
            {message.attachments && message.attachments.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {message.attachments.map((att) => (
                  <UserAttachment key={att.id} attachment={att} />
                ))}
              </div>
            ) : null}
            {message.content ? <div className="whitespace-pre-wrap break-words">{message.content}</div> : null}
          </div>
        ) : (message.segments ?? []).length === 0 && !message.content ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <span className="flex gap-0.5">
              <span className="inline-block h-1.5 w-1.5 animate-[pulse-subtle_1.4s_ease-in-out_infinite] rounded-full bg-current" />
              <span className="inline-block h-1.5 w-1.5 animate-[pulse-subtle_1.4s_ease-in-out_0.2s_infinite] rounded-full bg-current" />
              <span className="inline-block h-1.5 w-1.5 animate-[pulse-subtle_1.4s_ease-in-out_0.4s_infinite] rounded-full bg-current" />
            </span>
            <span>Thinking&hellip;</span>
          </div>
        ) : (message.segments ?? []).length > 0 ? (
          <div className="flex flex-col gap-1.5">
            {(message.segments ?? []).map((seg, i) =>
              seg.type === 'text' ? (
                <Markdown key={i} content={seg.content} />
              ) : (
                <ToolCallDetails key={seg.id} segment={seg} />
              ),
            )}
          </div>
        ) : (
          <Markdown content={message.content ?? ''} />
        )}
      </div>
    </div>
  );
});
